<template>
  <div class="page-container">
    <!-- Page Header -->
    <page-header
      icon="mdi-chart-line"
      :title="$t('report')"
      subtitle="Pregled narudžbina i statistike"
      icon-color="primary"
    >
      <template #actions>
        <v-row no-gutters justify="end">
          <excel-button @click="setExcel" class="mr-2" />
          <print-button @click="setPrinter" />
        </v-row>
      </template>
    </page-header>

    <!-- Toolbar -->
    <page-toolbar v-if="!device.isMobile" :searchable="false">
      <template #filters>
        <v-col cols="auto">
          <date-picker v-model="dates" color="white" />
        </v-col>

        <v-col cols="auto" md="3">
          <autocomplete
            :label="$t('restaurants')"
            v-model="selectedRestaurants"
            :items="restaurants"
            item-text="name"
            item-value="id"
            multiple
            hide-details
            color="white"
          />
        </v-col>

        <v-col cols="auto" md="auto">
          <shift-picker v-model="selectedShifts" multiple />
        </v-col>
        <v-col cols="auto" md="2">
          <text-field
            v-model="search"
            :label="$t('search')"
            hide-details
            kind="search"
            color="white"
          ></text-field>
        </v-col>
      </template>
    </page-toolbar>

    <!-- Mobile Filters -->
    <template v-else>
      <mobile-filters
        :filters="mobileFilterConfig"
        @update:filter="handleMobileFilterUpdate"
        @apply="onFiltersApply"
        @reset="resetFilters"
      />
    </template>

    <!-- Content Area -->
    <div :class="device.isMobile ? 'px-0 mt-2' : 'px-2 mt-2'">
      <v-tabs v-model="activeTab" background-color="primary" dark>
        <v-tab v-for="item in tabItems" :key="item.value" :value="item.value">
          <v-icon style="margin-right: 7px">{{ item.icon }}</v-icon>
          {{ item.tab }}
        </v-tab>
      </v-tabs>

      <v-tabs-items v-model="activeTab" :touchless="true">
        <v-tab-item v-for="item in tabItems" :key="item.value">
          <component v-if="activeTab === item.value" :is="item.component"></component>
        </v-tab-item>
      </v-tabs-items>
    </div>

    <!-- Mobile FAB Menu -->
    <v-speed-dial
      v-if="device.isMobile"
      v-model="fabMenu"
      fixed
      right
      direction="bottom"
      transition="slide-y-reverse-transition"
      style="top: 80px"
    >
      <template v-slot:activator>
        <v-btn v-model="fabMenu" color="grey" fab dark small depressed>
          <v-icon v-if="fabMenu">mdi-close</v-icon>
          <v-icon v-else>mdi-dots-vertical</v-icon>
        </v-btn>
      </template>

      <!-- Excel Button -->
      <v-btn fab small color="green darken-2" dark @click="setExcel()">
        <v-icon>mdi-microsoft-excel</v-icon>
      </v-btn>

      <!-- Print Button -->
      <v-btn fab small color="primary" dark @click="setPrinter()">
        <v-icon>mdi-printer</v-icon>
      </v-btn>
    </v-speed-dial>
  </div>
</template>

<script>
import PageHeader from "@/components/global/PageHeader.vue";
import PageToolbar from "@/components/global/PageToolbar.vue";
import EmptyState from "@/components/global/EmptyState.vue";
import MobileFilters from "@/components/global/MobileFilters.vue";
import ExcelButton from "@/components/global/buttons/GlobalButtonExcel.vue";
import PrintButton from "@/components/global/buttons/GlobalButtonPrint.vue";
import hasExcel from "@/mixins/hasExcel";
import hasPrinting from "@/mixins/hasPrinting";
import hasSnackbar from "@/mixins/hasSnackbar";
import { SHIFTS } from "@/constants/shifts";
import {
  CustomerReportTabOrders,
  CustomerReportTabCreditTransactions,
  CustomerReportTabTopup,
} from "@/components/customer/report/tabs";

export default {
  name: "CustomerReportPage",
  mixins: [hasExcel, hasPrinting, hasSnackbar],
  components: {
    PageHeader,
    PageToolbar,
    EmptyState,
    MobileFilters,
    ExcelButton,
    PrintButton,
    CustomerReportTabOrders,
    CustomerReportTabCreditTransactions,
    CustomerReportTabTopup,
  },
  data() {
    return {
      fabMenu: false,
      activeTab: 0,
    };
  },
  mounted() {
    // Set all restaurants as selected by default
    if (this.restaurants.length > 0 && this.selectedRestaurants.length === 0) {
      this.selectedRestaurants = this.restaurants.map((r) => r.id);
    }
  },
  computed: {
    restaurants() {
      return this.$store.state.company?.restaurants || [];
    },
    dates: {
      get() {
        const { from, to } = this.$store.state.report.filters.dates;
        return [from, to];
      },
      set(value) {
        if (Array.isArray(value) && value.length === 2) {
          this.$store.commit("report/SET_DATE_RANGE", {
            from: value[0],
            to: value[1],
          });
        }
      },
    },
    selectedRestaurants: {
      get() {
        return this.$store.state.report.selectedRestaurants || [];
      },
      set(value) {
        this.$store.commit("report/SET_SELECTED_RESTAURANTS", value);
      },
    },
    selectedShifts: {
      get() {
        return this.$store.state.report.filters.selectedShifts || [];
      },
      set(value) {
        this.$store.commit("report/SET_SELECTED_SHIFTS", value);
      },
    },
    search: {
      get() {
        return this.$store.state.report.filters.search || "";
      },
      set(value) {
        this.$store.commit("report/SET_SEARCH", value);
      },
    },
    tabItems() {
      return [
        {
          tab: this.$t("orders"),
          icon: "mdi-receipt-text",
          component: CustomerReportTabOrders,
          value: 0,
        },
        {
          tab: this.$t("credit_transactions"),
          icon: "mdi-bank-transfer",
          component: CustomerReportTabCreditTransactions,
          value: 1,
        },
        {
          tab: this.$t("add_credit"),
          icon: "mdi-cash-plus",
          component: CustomerReportTabTopup,
          value: 2,
        },
      ];
    },
    shifts() {
      return SHIFTS.map((shift) => ({
        id: shift.id,
        company: this.$t(shift.label),
      }));
    },
    formattedDateRange() {
      if (!this.dates || this.dates.length !== 2) return "";
      const [from, to] = this.dates;
      if (from === to) {
        return new Date(from).toLocaleDateString();
      }
      return `${new Date(from).toLocaleDateString()} - ${new Date(
        to
      ).toLocaleDateString()}`;
    },
    mobileFilterConfig() {
      return {
        dates: {
          type: "date",
          label: this.$t("date_range"),
          icon: "mdi-calendar",
          value: this.dates,
          chipText:
            this.dates && this.dates.length === 2
              ? `${new Date(this.dates[0]).toLocaleDateString()} - ${new Date(
                  this.dates[1]
                ).toLocaleDateString()}`
              : this.$t("select_date"),
          visible: true,
          clearable: false,
        },
        restaurants: {
          type: "autocomplete",
          label: this.$t("restaurants"),
          icon: "mdi-store",
          items: this.restaurants,
          itemText: "name",
          itemValue: "id",
          value: this.selectedRestaurants,
          multiple: true,
          chipText:
            this.selectedRestaurants.length > 0
              ? `${this.selectedRestaurants.length} ${this.$t("restaurants")}`
              : this.$t("all_restaurants"),
          visible: true,
          clearable: true,
        },
        shifts: {
          type: "autocomplete",
          label: this.$t("shifts"),
          icon: "mdi-clock-outline",
          items: this.shifts,
          itemText: "company",
          itemValue: "id",
          value: this.selectedShifts,
          multiple: true,
          chipText:
            this.selectedShifts.length > 0
              ? `${this.selectedShifts.length} ${this.$t("shifts")}`
              : this.$t("all_shifts"),
          visible: true,
          clearable: true,
        },
        search: {
          type: "search",
          label: this.$t("search"),
          icon: "mdi-magnify",
          value: this.search,
          chipText: this.search || this.$t("search"),
          visible: !!this.search,
          clearable: true,
        },
      };
    },
  },
  methods: {
    handleMobileFilterUpdate({ key, value }) {
      if (key === "dates") {
        this.dates = value;
      } else if (key === "restaurants") {
        this.selectedRestaurants = value;
      } else if (key === "shifts") {
        this.selectedShifts = value;
      } else if (key === "search") {
        this.search = value;
      }
    },
    resetFilters() {
      const today = new Date().toISOString().substr(0, 10);
      this.$store.commit("report/SET_DATE_RANGE", {
        from: today,
        to: today,
      });
      this.$store.commit("report/SET_SELECTED_RESTAURANTS", []);
      this.$store.commit("report/SET_SELECTED_SHIFTS", []);
      this.$store.commit("report/SET_SEARCH", "");
    },
  },
};
</script>

<style scoped>
.page-container {
  min-height: 100vh;
  background: #f5f5f5;
}
</style>
